import React, { useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Breadcrumbs,
  Link,
  Paper,
  CircularProgress,
  Alert,
  Grid,
  Divider,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Switch,
  FormControlLabel,
  Button,
  Modal,
  LinearProgress
} from '@mui/material';
import { NavigateNext as NavigateNextIcon } from '@mui/icons-material';
import { usePropertyById, PropertyFull } from '../../hooks/useMarketProperties';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { API_URL } from '../../utils/config';

type HubSpotPropertyOption = {
  name: string;
  label: string;
  options?: { label: string; value: string }[];
};

const currencyFormat = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined || value === '') return '';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num as number)) return String(value);
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(num as number);
};

const parseCurrency = (value: string): number | null => {
  const cleaned = value.replace(/[^0-9.\-]/g, '');
  const num = parseFloat(cleaned);
  return isNaN(num) ? null : num;
};

const required = (val: any) => val !== undefined && val !== null && String(val).trim() !== '';

export const NewAppraisalFromPropertyPage: React.FC = () => {
  const { propertyId } = useParams<{ propertyId: string }>();
  const navigate = useNavigate();
  const { data: property, isLoading, error } = usePropertyById(propertyId);
  const { authenticatedFetch } = useAuthenticatedFetch();

  // Deal panel state
  const [dealName, setDealName] = useState('');
  const [managingCompanyOptions, setManagingCompanyOptions] = useState<{ label: string; value: string }[]>([]);
  const [dealTypeOptions, setDealTypeOptions] = useState<{ label: string; value: string }[]>([]);
  const [managingCompany, setManagingCompany] = useState('');
  const [dealType, setDealType] = useState('');
  const [amount, setAmount] = useState('');
  const [retainer, setRetainer] = useState('');
  const [plusExpenses, setPlusExpenses] = useState(false);
  const [additionalHourlyFees, setAdditionalHourlyFees] = useState(false);
  const [hourlyRate, setHourlyRate] = useState('');
  const [dealDescription, setDealDescription] = useState('');
  const [intendedUsers, setIntendedUsers] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [errors, setErrors] = useState<{ 
    dealName: boolean; 
    managingCompany: boolean; 
    dealType: boolean; 
    amount: boolean; 
    retainer: boolean; 
    intendedUsers: boolean; 
  }>({
    dealName: false,
    managingCompany: false,
    dealType: false,
    amount: false,
    retainer: false,
    intendedUsers: false
  });

  // Static map state
  const [mapUrl, setMapUrl] = useState<string | null>(null);
  const [mapLoading, setMapLoading] = useState<boolean>(false);

  // Orchestration state
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [operationIdState, setOperationIdState] = useState<string | null>(null);
  const [operationProgress, setOperationProgress] = useState<{ step?: string; percent?: number; status?: string; error_message?: string; progress_text?: string } | null>(null);
  const pollRef = React.useRef<any>(null);

  const handleBreadcrumbClick = (event: React.MouseEvent<HTMLAnchorElement>, path: string) => {
    event.preventDefault();
    navigate(path);
  };

  // Fetch dropdown options from HubSpot
  React.useEffect(() => {
    const loadOptions = async () => {
      setLoadingOptions(true);
      try {
        const res = await authenticatedFetch(`${API_URL}/api/hubspot/properties/deals`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        });
        if (!res || !res.ok) {
          setLoadingOptions(false);
          return;
        }
        const json = await res.json();
        const props: HubSpotPropertyOption[] = json?.properties || [];
        const managingCo = props.find(p => p.name === 'managing_company');
        const dealTypeProp = props.find(p => p.name === 'dealtype');
        setManagingCompanyOptions(managingCo?.options || []);
        setDealTypeOptions(dealTypeProp?.options || []);
      } catch (e) {
        // swallow option errors for now
      } finally {
        setLoadingOptions(false);
      }
    };
    loadOptions();
  }, [authenticatedFetch]);

  // Fetch static map image from API when coordinates are available
  React.useEffect(() => {
    const fetchMap = async () => {
      if (!property?.location?.latitude || !property?.location?.longitude) {
        setMapUrl(null);
        return;
      }
      setMapLoading(true);
      try {
        const lat = property.location.latitude;
        const lng = property.location.longitude;
        const params = new URLSearchParams({
          lat: String(lat),
          lng: String(lng),
          radius_mi: '2',
          zoom: '13',
          width: '600',
          height: '200'
        });
        const res = await authenticatedFetch(`${API_URL}/api/maps/static?${params.toString()}`, { method: 'GET' });
        if (res && res.ok) {
          const json = await res.json();
          setMapUrl(json?.url || null);
        } else {
          setMapUrl(null);
        }
      } catch (e) {
        setMapUrl(null);
      } finally {
        setMapLoading(false);
      }
    };
    fetchMap();
  }, [authenticatedFetch, property?.location?.latitude, property?.location?.longitude]);

  const addressDisplay = useMemo(() => {
    if (!property) return '';
    const loc = property.location;
    return [loc.property_address, loc.city, loc.state, loc.zip].filter(Boolean).join(', ');
  }, [property]);

  const geographicPointDisplay = useMemo(() => {
    if (!property) return '-';
    const gp: any = (property.location as any)?.geographic_point;
    if (!gp) return '-';
    if (typeof gp === 'string') return gp;
    const lat = property.location?.latitude;
    const lng = property.location?.longitude;
    if (lat != null && lng != null) return `POINT(${lng} ${lat})`;
    try {
      return JSON.stringify(gp);
    } catch {
      return '-';
    }
  }, [property]);

  const onCreateDeal = async () => {
    // Validate all required deal panel fields except Hourly Rate and Deal Description
    const nextErrors = {
      dealName: !required(dealName),
      managingCompany: !required(managingCompany),
      dealType: !required(dealType),
      amount: !required(amount),
      retainer: !required(retainer),
      intendedUsers: !required(intendedUsers)
    };
    setErrors(nextErrors);
    if (Object.values(nextErrors).some(Boolean)) {
      setValidationError('Please complete all required fields in the Deal panel.');
      return;
    }
    setValidationError(null);
    setCreateError(null);
    setCreating(true);
    try {
      const body = {
        propertyId: property?.id ?? propertyId,
        deal: {
          dealName,
          managingCompany,
          dealType,
          amount: parseCurrency(amount) ?? 0,
          retainer: parseCurrency(retainer) ?? 0,
          plusExpenses,
          additionalHourlyFees,
          hourlyRate: parseCurrency(hourlyRate),
          dealDescription: dealDescription || null,
          intendedUsers
        }
      };

      const res = await authenticatedFetch(`${API_URL}/api/appraisal/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (!res || !res.ok) {
        const err = await res?.json().catch(() => ({} as any));
        throw new Error(err?.message || 'Failed to start appraisal creation');
      }
      const json = await res.json();
      const opId = json?.operationId as string | undefined;
      if (!opId) throw new Error('Missing operationId');
      setOperationIdState(opId);

      // Begin polling operations status every 2.5s
      const poll = async () => {
        try {
          const resp = await authenticatedFetch(`${API_URL}/api/operations/${opId}`, { method: 'GET' });
          if (!resp || !resp.ok) return; // keep polling
          const data = await resp.json();
          const op = data?.operation;
          if (op) {
            setOperationProgress({ step: op?.progress?.step, percent: op?.progress?.percent, status: op?.status, error_message: op?.error_message, progress_text: op?.progress?.progress_text || op?.progress_text });
            if (op.status === 'completed') {
              if (pollRef.current) clearInterval(pollRef.current);
              const appraisalId = op?.progress?.result?.appraisalId;
              if (appraisalId) navigate(`/appraisals/${appraisalId}/edit`);
              setCreating(false);
            } else if (op.status === 'error') {
              if (pollRef.current) clearInterval(pollRef.current);
              const appraisalId = op?.progress?.result?.appraisalId;
              if (appraisalId) navigate(`/appraisals/${appraisalId}/creation-error/${op.id}`);
              else navigate(`/appraisals/creation-error/${op.id}`);
              setCreating(false);
            }
          }
        } catch {}
      };
      poll();
      pollRef.current = setInterval(poll, 2500);
    } catch (e: any) {
      setCreateError(e?.message || 'Failed to start');
      setCreating(false);
    }
  };

  React.useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  return (
    <>
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Breadcrumbs 
        separator={<NavigateNextIcon fontSize="small" />} 
        aria-label="breadcrumb"
        sx={{ mb: 2 }}
      >
        <Link
          color="inherit"
          href="/"
          onClick={(e) => handleBreadcrumbClick(e, '/')}
          sx={{ cursor: 'pointer', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
        >
          Home
        </Link>
        <Link
          color="inherit"
          href="/appraisals"
          onClick={(e) => handleBreadcrumbClick(e, '/appraisals')}
          sx={{ cursor: 'pointer', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
        >
          Appraisals
        </Link>
        <Link
          color="inherit"
          href="/appraisals/new"
          onClick={(e) => handleBreadcrumbClick(e, '/appraisals/new')}
          sx={{ cursor: 'pointer', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
        >
          New Appraisal
        </Link>
        <Typography color="text.primary">From Property</Typography>
      </Breadcrumbs>

      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}>
          Create New Appraisal
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Pre-filled from market analytics property
        </Typography>
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 2 }}>{String(error.message || error)}</Alert>
      ) : (
        <>
          {/* Deal Panel */}
          <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Create HubSpot Deal</Typography>
            {validationError && (
              <Alert severity="warning" sx={{ mb: 2 }}>{validationError}</Alert>
            )}
            {createError && (
              <Alert severity="error" sx={{ mb: 2 }}>{createError}</Alert>
            )}
            {/* Creating overlay handled by Modal below */}
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Deal Name"
                  fullWidth
                  value={dealName}
                  error={errors.dealName}
                  helperText={errors.dealName ? 'Required' : ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    setDealName(val);
                    if (errors.dealName && required(val)) setErrors(prev => ({ ...prev, dealName: false }));
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth disabled={loadingOptions} error={errors.managingCompany}>
                  <InputLabel>Managing Company</InputLabel>
                  <Select
                    label="Managing Company"
                    value={managingCompany}
                    onChange={(e) => {
                      const val = e.target.value as string;
                      setManagingCompany(val);
                      if (errors.managingCompany && required(val)) setErrors(prev => ({ ...prev, managingCompany: false }));
                    }}
                  >
                    {managingCompanyOptions.map(opt => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                  {errors.managingCompany && <FormHelperText>Required</FormHelperText>}
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth disabled={loadingOptions} error={errors.dealType}>
                  <InputLabel>Deal type</InputLabel>
                  <Select
                    label="Deal type"
                    value={dealType}
                    onChange={(e) => {
                      const val = e.target.value as string;
                      setDealType(val);
                      if (errors.dealType && required(val)) setErrors(prev => ({ ...prev, dealType: false }));
                    }}
                  >
                    {dealTypeOptions.map(opt => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                  {errors.dealType && <FormHelperText>Required</FormHelperText>}
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Amount"
                  fullWidth
                  value={amount}
                  error={errors.amount}
                  helperText={errors.amount ? 'Required' : ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    setAmount(val);
                    if (errors.amount && required(val)) setErrors(prev => ({ ...prev, amount: false }));
                  }}
                  placeholder="$0"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Retainer"
                  fullWidth
                  value={retainer}
                  error={errors.retainer}
                  helperText={errors.retainer ? 'Required' : ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    setRetainer(val);
                    if (errors.retainer && required(val)) setErrors(prev => ({ ...prev, retainer: false }));
                  }}
                  placeholder="$0"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={<Switch checked={plusExpenses} onChange={(e) => setPlusExpenses(e.target.checked)} />}
                  label="Plus Expenses"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={<Switch checked={additionalHourlyFees} onChange={(e) => setAdditionalHourlyFees(e.target.checked)} />}
                  label="Additional Hourly Fees"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Hourly Rate"
                  fullWidth
                  value={hourlyRate}
                  onChange={(e) => setHourlyRate(e.target.value)}
                  placeholder="$0"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Deal Description"
                  fullWidth
                  multiline
                  minRows={3}
                  value={dealDescription}
                  onChange={(e) => setDealDescription(e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Intended Users"
                  fullWidth
                  value={intendedUsers}
                  error={errors.intendedUsers}
                  helperText={errors.intendedUsers ? 'Required' : ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    setIntendedUsers(val);
                    if (errors.intendedUsers && required(val)) setErrors(prev => ({ ...prev, intendedUsers: false }));
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Button variant="contained" color="primary" onClick={onCreateDeal} disabled={creating}>
                  {creating ? 'Creating…' : 'Create HubSpot Deal'}
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* Property Information */}
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Property</Typography>
            {!property ? (
              <Typography variant="body2" color="text.secondary">No property data</Typography>
            ) : (
              <>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={8}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6} md={6}>
                        <Typography variant="subtitle2" color="text.secondary">Property ID</Typography>
                        <Typography variant="body1">{property.property_id || property.id}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={6}>
                        <Typography variant="subtitle2" color="text.secondary">Name</Typography>
                        <Typography variant="body1">{property.property_name || '-'}</Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="text.secondary">Address</Typography>
                        <Typography variant="body1">{addressDisplay || '-'}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="subtitle2" color="text.secondary">Latitude</Typography>
                        <Typography variant="body1">{property.location?.latitude ?? '-'}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="subtitle2" color="text.secondary">Longitude</Typography>
                        <Typography variant="body1">{property.location?.longitude ?? '-'}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="subtitle2" color="text.secondary">Geographic_Point</Typography>
                        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{geographicPointDisplay}</Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ width: '100%', height: 200, borderRadius: 1, overflow: 'hidden', bgcolor: 'grey.100', border: '1px solid', borderColor: 'grey.300', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {mapLoading ? (
                        <CircularProgress size={24} />
                      ) : mapUrl ? (
                        <Box component="img" src={mapUrl} alt="Property map" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <Typography variant="body2" color="text.secondary" align="center">
                          Map preview unavailable
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="subtitle2" color="text.secondary">Country</Typography>
                    <Typography variant="body1">{property.market?.country?.name || '-'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="subtitle2" color="text.secondary">Market</Typography>
                    <Typography variant="body1">{property.market?.name || '-'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="subtitle2" color="text.secondary">Rooms</Typography>
                    <Typography variant="body1">{property.details?.rooms ?? '-'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="subtitle2" color="text.secondary">Year Built</Typography>
                    <Typography variant="body1">{property.details?.year_built ?? '-'}</Typography>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="subtitle2" color="text.secondary">Owner</Typography>
                    <Typography variant="body1">{property.ownership?.owner_name || '-'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="subtitle2" color="text.secondary">Management Company</Typography>
                    <Typography variant="body1">{property.ownership?.property_manager_name || '-'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="subtitle2" color="text.secondary">Operator</Typography>
                    <Typography variant="body1">{property.ownership?.hotel_operator || '-'}</Typography>
                  </Grid>
                </Grid>
              </>
            )}
          </Paper>
        </>
      )}
    </Container>

    {/* Blocking overlay while creating */}
    <Modal
      open={creating}
      aria-labelledby="creating-appraisal-title"
      aria-describedby="creating-appraisal-description"
      disableEscapeKeyDown
      slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(0,0,0,0.6)' } } }}
    >
      <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 'min(560px, 90vw)', bgcolor: 'background.paper', borderRadius: 2, boxShadow: 24, p: 4, outline: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <CircularProgress size={28} />
          <Typography id="creating-appraisal-title" variant="h6">Creating HubSpot Deal & Appraisal</Typography>
        </Box>
        <Typography id="creating-appraisal-description" variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {operationProgress?.progress_text || operationProgress?.step || 'Starting…'}
        </Typography>
        {typeof operationProgress?.percent === 'number' ? (
          <LinearProgress variant="determinate" value={operationProgress.percent} />
        ) : (
          <LinearProgress />
        )}
      </Box>
    </Modal>
    </>
  );
};

export default NewAppraisalFromPropertyPage;


